import { Button, Dialog, DialogContent, DialogTitle, withStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import Stepper from '../../../../common/Stepper';
import OptimizationConstraints from './components/OptimizationConstraints';
import { inject, observer } from 'mobx-react';
import DialogActionContainer from './components/DialogActionContainer';
import ValidationsContainer from './components/validationSections';
import { postData } from '../../../../../api/componentAPIs';
import { getStepsData } from './utils';
import ReviewProgressAnimationDialog from './components/ReviewProgressAnimationDialog';
import { useDynamicRoutingValidationContext } from './context/DynamicRoutingValidationContext';
import { RMAXLogger } from '../../../../../services/LoggerService/RMAXLogger';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import { convertMilliSecondsToSeconds } from '../../../../../utilities/appHelper';
import Events from '../../../../../utilities/EventEmitter';
import {
  UNAPPOINTED_PROS_ROUTE_MOVE_MESSAGES,
  assignUnappointedProsToCityRoute,
  buildValidationCompleteLogContext,
  getCityRouteForUnappointedProsFromSteps,
  shouldMoveUnappointedProsBeforeOptimization
} from './components/validationSections/unappointedProsFlowUtils';

const styles = (theme) => ({
  dialogContainer: {
    paddingBottom: 24,
    userSelect: 'none',
    backgroundColor: theme.ModalBGColor
  },
  dialogTitle: {
    fontWeight: 600,
    color: theme.HeaderSelectedText,
    padding: '18px 20px 6px',
    borderBottom: `1px solid ${theme.iconColor}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerSkipButton: {
    fontSize: theme.textFontSize
  },
  stepperWrapper: {
    margin: 16
  },
  dialogContent: {
    color: theme.gridTextColor,
    fontSize: 12,
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 12
  },
  closeIconStyle: {
    padding: '0px',
    float: 'right',
    color: theme.gridTextColor,
    '&:hover': {
      backgroundColor: theme.ModalBGColor,
      color: theme.HeaderSelectedText
    }
  },
  closeIcon: {
    fontSize: 24
  },
  verticalDivider: {
    width: 1,
    backgroundColor: theme.TextColor
  },
  actionContainer: {
    width: '60%',
    margin: 0
  },
  dialogActionStyle: {
    padding: '0px 24px',
    margin: 0
  },
  dialogButtonStyle: {
    fontSize: 14,
    textTransform: 'none',
    borderRadius: '2px',
    float: 'right',
    width: '35%'
  }
});

const DynamicRoutingValidation = ({
  show,
  classes,
  onClose,
  userData,
  inputData,
  enableMaxStopsPerRouteInput,
  themeName,
  glEventHub,
  CommonStore,
  IBPlanningStore,
  enqueueSnackbar,
  runOptimization,
  cancelOptimization,
  handleDynamicRoutingError,
  isDynamicRoutingTriggeredByShipmentSelection
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepsData, setStepsData] = useState([]);
  const [isInputValidationInProgress, setIsInputValidationInProgress] = useState(false);
  const [isRouteAssignmentInProgress, setIsRouteAssignmentInProgress] = useState(false);
  const [reviewTime, setReviewTime] = useState(0);
  const [validationStartTime, setValidationStartTime] = useState(0);
  const { excludedPros, selectedProsForCityRoute } = useDynamicRoutingValidationContext();

  useEffect(() => {
    if (show) getInputValidationData(inputData);
  }, [show]);

  const getInputValidationData = (data = {}) => {
    setIsInputValidationInProgress(true);
    const reviewStartTime = Date.now();
    const payload = {
      ...data,
      terminal: userData.userProfile.terminal.code,
      scenarioId: userData.userProfile.scenarioID
    };
    postData('GetInputValidationForDynamicRouting', payload)
      .then((response) => {
        setIsInputValidationInProgress(false);
        if (response && Math.trunc(response.status / 100) === 2) {
          const steps = getStepsData(response.data);
          if (steps.length === 0) {
            completeValidation();
            return;
          }
          setStepsData(steps);
          setCurrentStep(0);
          const reviewEndTime = Date.now();
          setReviewTime(convertMilliSecondsToSeconds(reviewEndTime - reviewStartTime));
          setValidationStartTime(reviewEndTime);
        } else {
          handleError(response);
        }
      })
      .catch((error) => {
        setIsInputValidationInProgress(false);
        handleError();
      });
  };

  const handleError = (response) => {
    const statusCode = response?.status;
    const errorMessage = response?.response?.data?.error?.message;

    RMAXLogger.error('Dynamic routing validation failed', {
      statusCode,
      errorMessage
    });

    onClose();
    if (statusCode && statusCode >= 500 && statusCode < 600) { // All 5XX server errors
      enqueueSnackbar('Input validation failed', { variant: 'error' });
      runOptimization();
    } else if (statusCode !== 403) {
      handleDynamicRoutingError(response);
    }
  };

  const goNext = () => {
    if (currentStep === stepsData.length - 1) return;
    setCurrentStep((step) => step + 1);
  };

  const goBack = () => {
    if (currentStep === 0) return;
    setCurrentStep((step) => step - 1);
  };

  const handleRouteAssignmentFailure = ({ response, routeAssignmentPayload, error } = {}) => {
    const statusCode = response?.status;

    RMAXLogger.error('Unappointed pros route assignment failed', {
      statusCode,
      cityRouteName: routeAssignmentPayload?.routeName,
      proCount: routeAssignmentPayload?.pros?.length ?? 0,
      errorMessage: error?.message
    });
    enqueueSnackbar('Failed to move selected shipments to city route', { variant: 'error' });
    if (response && statusCode !== 403) {
      handleDynamicRoutingError(response);
    }
  };

  const refreshStoreAfterRouteAssignment = (prosToMoveToCityRoute) => {
    Events.emit('RefreshIBStore', {
      showNotification: false,
      selectedRoutesList: inputData?.routeNames,
      selectedShipmentsList: prosToMoveToCityRoute
    });
  };

  const completeValidation = async () => {
    const excludedProsList = [...excludedPros];
    const prosToMoveToCityRoute = [...selectedProsForCityRoute];
    const cityRouteForUnappointedPros = getCityRouteForUnappointedProsFromSteps(stepsData);
    const shouldMovePros = shouldMoveUnappointedProsBeforeOptimization(
      prosToMoveToCityRoute,
      cityRouteForUnappointedPros
    );

    RMAXLogger.info(
      'Dynamic routing validation complete',
      buildValidationCompleteLogContext({
        reviewTime,
        validationStartTime,
        excludedProsList,
        prosToMoveToCityRoute,
        cityRouteForUnappointedPros,
        routeNames: inputData?.routeNames
      })
    );

    if (!shouldMovePros) {
      runOptimization({ excludedPros: excludedProsList });
      return;
    }

    setIsRouteAssignmentInProgress(true);
    try {
      const assignmentResult = await assignUnappointedProsToCityRoute({
        scenarioId: userData.userProfile.scenarioID,
        cityRouteForUnappointedPros,
        pros: prosToMoveToCityRoute,
        postData
      });

      if (!assignmentResult.success) {
        handleRouteAssignmentFailure(assignmentResult);
        return;
      }

      refreshStoreAfterRouteAssignment(prosToMoveToCityRoute);
      runOptimization({ excludedPros: excludedProsList });
    } catch (error) {
      handleRouteAssignmentFailure({ error });
    } finally {
      setIsRouteAssignmentInProgress(false);
    }
  };

  const skipAdjustment = () => {
    RMAXLogger.info('Skip dynamic routing validation');
    runOptimization();
  };

  if (isInputValidationInProgress || isRouteAssignmentInProgress) {
    return (
      <ReviewProgressAnimationDialog
        show
        title={
          isRouteAssignmentInProgress ? UNAPPOINTED_PROS_ROUTE_MOVE_MESSAGES.title : undefined
        }
        subtitle={
          isRouteAssignmentInProgress ? UNAPPOINTED_PROS_ROUTE_MOVE_MESSAGES.subtitle : undefined
        }
      />
    );
  }

  return (
    <Dialog
      open={show}
      onClose={onClose}
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.dialogContainer, root: classes.dialogRoot }}
      className={themeName}
      fullScreen
      aria-labelledby="dynamic-routing-validation">
      <DialogTitle classes={{ root: classes.dialogTitle }} disableTypography>
        <span>Adjust Shipment Properties</span>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          classes={{ root: classes.headerSkipButton }}
          onClick={skipAdjustment}>
          Skip Adjustment & Optymize
        </Button>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }} className="customScroll">
        <div className={classes.stepperWrapper}>
          <Stepper steps={stepsData} activeStep={currentStep} />
        </div>
        <OptimizationConstraints
          inputData={inputData}
          CommonStore={CommonStore}
          enableMaxStopsPerRouteInput={enableMaxStopsPerRouteInput}
        />
        <ValidationsContainer
          currentStep={currentStep}
          stepsData={stepsData}
          glEventHub={glEventHub}
          userData={userData}
          IBPlanningStore={IBPlanningStore}
          enqueueSnackbar={enqueueSnackbar}
        />
      </DialogContent>
      <DialogActionContainer
        currentStep={currentStep}
        stepsLength={stepsData.length}
        goNext={goNext}
        goBack={goBack}
        runOptimizer={completeValidation}
        onCancel={cancelOptimization}
        goBackToTrailerInput={onClose}
        showCancelButton={!isDynamicRoutingTriggeredByShipmentSelection}
      />
    </Dialog>
  );
};

export default inject(
  'CommonStore',
  'IBPlanningStore'
)(observer(compose(withStyles(styles), withSnackbar)(DynamicRoutingValidation)));
